/**
 * The caller's CenoX AI conversations, as the server lists them.
 *
 * One query shared by Chat, Home and the shell's activity panel. The key is the
 * one ChatView has always used (with an empty search), so starting, deleting or
 * sharing a thread in Chat refreshes Home without a second invalidation.
 *
 * What comes back is already only what the caller may read — their own threads
 * and the ones colleagues shared — because a row-level policy decides that, not
 * this file. The server orders by latest activity and caps the list.
 */

import { useQuery } from '@tanstack/react-query';
import type { AiConversationView } from '@cenox/shared';
import { api } from './api';
import { useAuth, useBusinessPath } from './auth';

export function useAiConversations(enabled: boolean) {
  const { business } = useAuth();
  const path = useBusinessPath();

  return useQuery({
    queryKey: ['ai-conversations', business?.businessId, ''],
    queryFn: () => api.get<{ data: AiConversationView[] }>(path('/ai/conversations')),
    enabled: Boolean(business) && enabled,
  });
}

/** When a thread last moved: its latest message, or when it was started. */
export const lastActive = (c: AiConversationView): string => c.lastMessageAt ?? c.createdAt;
